import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Numeral from "../../../components/numeral/Numeral";
import { Primary } from "../../../components/buttons/Primary";
import { ArrowLongRightIcon } from "@heroicons/react/24/outline";
import { HttpRequest } from "../../../services/httpRequest";
import { validateSessionAndJwtToken } from "../../../utils/validateTokenSession";
import { Fifth } from "../../../components/buttons/Fifth";
import { CreditUserContext } from "../../../context/CreditUser/CreditUserContext";
import { returnToSignin } from "../../../utils/returnToSign";
import { DashboardMyCredit } from "../../../components/backgrounds/DashboardMyCredit";
import { useCredit } from "../../../context/creditContext";
import { IndexedDBService } from "../../../services/indexedDbService";
import { determinaCreditoAMostrar, loadCredits } from "../credits/utils";
import { SkeletonPayloginPage } from "./components/skeleton_paylogin_page";

const indexedDbService = new IndexedDBService();

export function PayLoginPage(props) {
  const { updateDeudaHoyBackend, updateCreditUser } =
    useContext(CreditUserContext);
  const { disclosures, creditInfo, due } = useCredit();
  let navigate = useNavigate();
  const [basicInfo, setBasicInfo] = useState({});
  const [creditsInfo, setCreditsInfo] = useState({});
  const [, setUserInfoAuth] = useState({});
  const [costs, setCosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deudaAFecha, setDeudaAFecha] = useState(0);
  const [, setInfoCupo] = useState({});
  const [hasSaldoCapital, setHasSaldoCapital] = useState(true);
  const [hasCreditVigent, setHasCreditVigent] = useState(false);

  const loadCreditById = useCallback(async () => {
    try {
      const data = await HttpRequest.getCreditById(
        localStorage.getItem("credit_id")
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoUserAuth = useCallback(async () => {
    try {
      const data = await HttpRequest.getUserAuth(localStorage.getItem("doc"));
      return data.message.user;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoUser = useCallback(async () => {
    try {
      const data = await HttpRequest.getInfoUser(localStorage.getItem("doc"));
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoQuota = useCallback(async () => {
    try {
      const data = await HttpRequest.getQuotaStatus(
        localStorage.getItem("doc")
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const configData = useCallback(async (credit, infoCupo) => {
    let costsFeatures;
    let infoDeudaBackend;
    let deudahoy;
    try {
      let versionTarifas = credit?.versionTarifas;
      let responseData = versionTarifas === "2" ? await HttpRequest.calcularDeudaV2({ credit: credit, infoCupo: infoCupo, }) :
        await HttpRequest.calcularDeuda({ credit: credit, infoCupo: infoCupo, });
      //let responseData = await HttpRequest.calcularDeudaV2({ credit, infoCupo });
      if (responseData?.message?.deuda?.hasSaldoCapital === true) {
        infoDeudaBackend = responseData.message.deuda;
        costsFeatures = infoDeudaBackend?.costsFeature;
        deudahoy = infoDeudaBackend?.deudaAFecha;
        setCosts(costsFeatures);
        setDeudaAFecha(deudahoy);
        updateDeudaHoyBackend(infoDeudaBackend);
        return;
      } else {
        setHasSaldoCapital(false);
        return;
      }
    } catch (error) {
      console.log(error);
    }
  }, [updateDeudaHoyBackend]);

  const handleUpdateDataIndexDb = useCallback(async (_total) => {
    try {
      let item = await indexedDbService.findItem('id', localStorage.getItem('credit_id'));
      if (item) {
        indexedDbService.updateItem(item.id, { amount: _total, hasExtend: false });
      } else {
        await indexedDbService.addItem({ amount: _total, id: localStorage.getItem('credit_id'), hasExtend: false });
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const determineIfCreditIsPendingToSign = useCallback(async () => {
    try {
      let credits = await loadCredits();
      let currentCredit = determinaCreditoAMostrar(credits);
      if (currentCredit[0]?.status === "Pendiente de firmar") {
        return navigate("/request-credit?renovar=true");
      }
    } catch (error) {
      console.log(error);
      return;
    }
  }, [navigate]);

  useEffect(() => {
    const loadQuotaStatus = async () => {
      let quotaStatus = await loadInfoQuota();
      return quotaStatus;
    };
    const validSessionAndTokenJwt = async () => {
      try {
        let isValidToken = await validateSessionAndJwtToken();
        return isValidToken;
      } catch (error) {
        return false;
      }
    };

    const _loadCreditById = async () => {
      let credit = await loadCreditById();
      return credit;
    };

    const loadUserAuth = async () => {
      let userAuth = await loadInfoUserAuth();
      return userAuth;
    };

    const loadInfo = async () => {
      let info = await loadInfoUser();
      return info;
    };

    const tieneCreditoVigente = (creditos = []) => {
      let creditosVigentes = creditos.filter(
        (credito) => credito?.status === "Vigente"
      );
      if (creditosVigentes.length === 0) {
        return false;
      } else {
        return true;
      }
    };

    validSessionAndTokenJwt()
      .then(async (isValidToken) => {
        let info;
        let userAuth;
        let creditData;
        if (isValidToken) {
          try {
            determineIfCreditIsPendingToSign();
            userAuth = await loadUserAuth();
            info = await loadInfo();
            setBasicInfo(info);
            setUserInfoAuth(userAuth);
            let cupo = await loadQuotaStatus();
            setInfoCupo(cupo);
            creditData = await _loadCreditById();
            let existeCreditoVigente = tieneCreditoVigente([creditData]);
            setHasCreditVigent(existeCreditoVigente);
            if (!creditData) {
              alert("No tienes creditos activos.");
            } else {
              setCreditsInfo(creditData);
              updateCreditUser(creditData);
              configData(creditData, cupo);
            }
            setIsLoading(false);
          } catch (error) {
            console.log(error);
            return;
          }
        } else {
          returnToSignin(navigate);
        }
      })
      .catch((e) => {
        returnToSignin(navigate);
      });
  }, [
    navigate,
    updateCreditUser,
    loadInfoQuota,
    loadCreditById,
    loadInfoUserAuth,
    loadInfoUser,
    determineIfCreditIsPendingToSign,
    configData,
  ]);

  if (isLoading) {
    return (
      <SkeletonPayloginPage />
    );
  }

  return (
    <DashboardMyCredit
      name={basicInfo?.firstname ?? ""}
      balance={creditsInfo?.status === "Cancelado" ? 0 : deudaAFecha}
      current={"2"}
      title={"Pagar mi cuota"}
      disclosures={disclosures}
      creditInfo={creditInfo}
      due={due}
      main={
        <>
          <div className="w-full h-full md:h-auto mt-10 rounded-3xl pb-32 md:pb-0 bg-white md:bg-transparent">
            <div className="sm:grid justify-center">
              <div className="sm:w-110 w-full px-8 py-10 bg-white md:shadow-lg rounded-3xl">
                {creditsInfo?.status === "Cancelado" ||
                  creditsInfo?.status === "Solicitud rechazada" ? (
                  <div className="text-center">
                    <h2>No tienes créditos activos en este momento</h2>
                    <div className="mx-auto w-4/5 sm:w-auto xl:w-1/2 pt-8">
                      <a href="/renovar">
                        <Primary
                          text={"Consultar cupo"}
                          icon={<ArrowLongRightIcon className="w-6 h-auto" />}
                        />
                      </a>
                    </div>
                  </div>
                ) : hasSaldoCapital === false ? (
                  <div className="text-center">
                    <h2>
                      Lo sentimos. ha ocurrido un error al consultar tu deuda a
                      hoy, por favor comunicate con el área de soporte para
                      revisar a fondo, gracias por tu comprensión.
                    </h2>
                    <div className="mx-auto w-4/5 sm:w-auto xl:w-1/2 pt-8"></div>
                  </div>
                ) : !hasCreditVigent ? (
                  <div className="text-center">
                    <h2>No tienes un crédito vigente en este momento</h2>
                    <div className="mx-auto w-4/5 sm:w-auto xl:w-1/2 pt-8">
                      <a href="/renovar">
                        <Primary
                          text={"Consultar cupo"}
                          icon={<ArrowLongRightIcon className="w-6 h-auto" />}
                        />
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 className="font-bold text-xl md:px-4 block w-full">
                        Crédito N.{creditsInfo?.credit_id.substring(0, 8) || ""}
                      </h3>
                      <div className="md:px-4 py-4">
                        {costs && costs.map((cost, index) => (
                          <div
                            key={index}
                            className="flex justify-between gap-2 w-full pt-1 text-grayP"
                          >
                            <span>{cost.value === 0 ? "" : cost.item} </span>
                            {
                              cost.item === "DCTO firma electrónica" && cost.value > 0 ? (
                                <span>{"-"}<Numeral format={"$0,0"} value={cost.value} /></span>
                              ) : (
                                <span>{cost.item === "DCTO recompensa" ? "-" : ""}<Numeral format={"$0,0"} value={cost.value} /></span>
                              )
                            }

                          </div>
                        ))}
                      </div>
                      <div>
                        <div className="flex gap-2 text-xl font-bold justify-between pt-4 md:px-4">
                          <span>Total a pagar</span>
                          <Numeral format={"$0,0"} value={deudaAFecha} />
                        </div>
                      </div>
                      <div className="text-grayP flex gap-1 md:px-4">
                        Fecha límite de pago{" "}
                        <div className="font-bold text-tdark">
                          {creditsInfo?.paydayLimit}
                        </div>
                      </div>
                      {creditsInfo?.diasMora && creditsInfo.diasMora > 0 ? (
                        <div className="text-red font-bold px-4">
                          {`Tienes ${creditsInfo.diasMora} días en mora`}
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                    <div className="flex gap-2 pt-8 sm:justify-center w-full">
                      <div className="pb-2 sm:pb-0 w-full">
                        <a
                          href={"/extencion"}
                          replace={false}
                          state={{
                            data: {
                              isPaymentWompi: false,
                              amount: deudaAFecha,
                              doc: creditsInfo?.documento,
                              name: basicInfo?.firstname ?? "",
                            },
                          }}
                        >
                          <Fifth text={"Ampliar plazo"} />
                        </a>
                      </div>
                      <div>
                        <a
                          onClick={() => handleUpdateDataIndexDb(deudaAFecha)}
                          href={"/medios-de-pago"}
                          state={{
                            data: {
                              isPaymentWompi: false,
                              credit_id: creditsInfo?.credit_id,
                              amount: deudaAFecha,
                              doc: creditsInfo?.documento,
                              name: basicInfo?.firstname,
                              lastname: basicInfo?.firstlastname,
                              extendPlazo: false,
                            },
                          }}
                        >
                          <Primary
                            credit_id={creditsInfo?.credit_id}
                            isPaymentWompi={false}
                            amount={deudaAFecha}
                            doc={creditsInfo?.documento}
                            text={"Pagar"}
                            icon={<ArrowLongRightIcon className="w-6 h-auto" />}
                            lastname={basicInfo.firstlastname}
                          />
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      }
    />
  );
}
